// Custom hook for package matching logic
import { useMemo } from "react";
import type { ChannelAvailability, ChannelCardV2, PackageReference, PackageSummary } from "lib/helper/channel-interactive-tool-v2";
import type { ShowData, PackageMatchResult } from "./types";

/**
 * Strips diacritics/accents and lowercases for robust matching.
 * "Cinemáx" → "cinemax", "CINEMAX" → "cinemax", "TBS" → "tbs"
 */
export const normalizeForMatch = (s: string): string => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

// Availability precedence, matching channels.ts (included > addon > promo).
const AVAILABILITY_RANK: Record<ChannelAvailability, number> = { included: 3, addon: 2, promo: 1 };
const rankOf = (ref: PackageReference): number => AVAILABILITY_RANK[ref.availability] ?? 0;

/**
 * Collect a package ref into the per-id accumulator (the union across matched
 * channels), keeping the highest-availability ref when the same package arrives via
 * more than one channel — so the modal's list + legal text don't depend on
 * channelMap iteration order (WAT-11007 #7-DTVI).
 */
const addPackageRef = (byId: Map<string, PackageReference>, ref: PackageReference): void => {
  if (!ref?.id) {
    return;
  }
  const current = byId.get(ref.id);
  if (!current || rankOf(ref) > rankOf(current)) {
    byId.set(ref.id, ref);
  }
};

/**
 * Extract network names from show data.
 * Returns a Map of normalized-network-name → original-cased-name so that:
 *   - lookups against normalized channelMap labels always work (handles accents, case)
 *   - the original casing can be recovered for display ("Watch on CINEMAX")
 */
const extractNetworkNames = (show: ShowData): Map<string, string> => {
  // key = normalized (no accents, lowercase), value = original cased name from API
  const networkNames = new Map<string, string>();

  const add = (name: string) => {
    if (name) {
      networkNames.set(normalizeForMatch(name), name.trim());
    }
  };

  // networkAttributionName
  if (show?.networkAttributionName) {
    add(show.networkAttributionName);
  }

  // consumables — API sends mixed case e.g. "CINEMAX", "HBO Max"
  if (show?.consumables && Array.isArray(show.consumables)) {
    show.consumables.forEach(item => {
      const networkName =
        item?.provider?.networkName ||
        item?.channel?.networkName ||
        item?.channel?.callSign ||
        item?.channel?.name;
      if (networkName) {
        add(networkName);
      }
    });
  }

  // For SERIES: root-level channel/provider
  if (show?.resourceType === "SERIES") {
    const seriesNetworkName =
      show?.provider?.networkName || show?.channel?.networkName || show?.channel?.name;
    if (seriesNetworkName) {
      add(seriesNetworkName);
    }

    // seasons[0].contents[0]
    if (show?.seasons?.[0]?.contents?.[0]) {
      const episode = show.seasons[0].contents[0];
      const episodeNetwork =
        episode?.provider?.networkName ||
        episode?.channel?.networkName ||
        episode?.channel?.name;
      if (episodeNetwork) {
        add(episodeNetwork);
      }
    }
  }

  return networkNames;
};

/**
 * Map the show's channel callSigns → display network name. callSign is the STABLE
 * join between the OV show and the channels-service feed: channel *names* drift by
 * resolution (OV "TNT" vs feed "TNT HD"), but the callSign ("TNTHD") is identical on
 * both, so it matches where a name match can't (WAT-11007 #7-DTVI).
 */
const extractChannelCallSigns = (show: ShowData): Map<string, string> => {
  const byCallSign = new Map<string, string>();
  const add = (ch?: { callSign?: string; networkName?: string; name?: string }) => {
    if (ch?.callSign) {
      byCallSign.set(ch.callSign.toUpperCase(), ch.networkName || ch.name || ch.callSign);
    }
  };
  show?.consumables?.forEach(item => add(item?.channel));
  add(show?.channel);
  if (show?.resourceType === "SERIES") {
    add(show?.seasons?.[0]?.contents?.[0]?.channel);
  }
  return byCallSign;
};

/**
 * Custom hook for matching shows with packages
 */
export const usePackageMatching = (
  show: ShowData | null,
  channelMap: Record<string, ChannelCardV2>,
  packageSummaries: Record<string, PackageSummary>,
  ovChnlPageData: any,
  chnlSrc: string,
  genreConfig: any
): PackageMatchResult => useMemo(() => {
  const emptyResult: PackageMatchResult = {
    signaturePackages: [],
    genrePackages: [],
    packageNetworkMap: {},
    matchedChannelLabels: []
  };

  if (!show) {
    return emptyResult;
  }
  if (!channelMap || Object.keys(channelMap).length === 0) {
    return emptyResult;
  }

  // networkNamesMap: lowercase → original-cased (e.g. "cinemax" → "CINEMAX")
  const networkNamesMap = extractNetworkNames(show);
  // callSign is the stable join — try it even when the show exposes no usable names.
  const callSignToNetwork = extractChannelCallSigns(show);
  if (networkNamesMap.size === 0 && callSignToNetwork.size === 0) {
    return emptyResult;
  }

  // Match network names with channelMap
  const packageToNetworkMap: Record<string, string[]> = {};
  // Accumulate package refs across EVERY matched channel, deduped by id. A show on
  // multiple networks (e.g. a movie on TBS + Cinemax) belongs to the UNION of those
  // channels' packages; assigning per-channel kept only the LAST match, making the
  // result depend on channelMap iteration order — the WAT-11007 #7-DTVI stage/prod gap.
  // addPackageRef builds the union of matched channels' packages, deduped by id with
  // the strongest availability kept — making the result channelMap-order-independent.
  const signatureById = new Map<string, PackageReference>();
  const genreById = new Map<string, PackageReference>();
  // Track the exact channel.label values that matched (e.g. "Cinemáx")
  // so MovieModalV2 can slice the svodPromoMap to find per-package promo text.
  const matchedChannelLabels: string[] = [];

  Object.values(channelMap).forEach(channel => {
    // Normalize the channel label the same way we normalized network names:
    // strips accents (á→a) + lowercase, so "Cinemáx" → "cinemax" matches "CINEMAX" → "cinemax"
    const channelLabel = normalizeForMatch(channel.label || "");

    // matchedDisplayName: what we show in "Watch on <X>" — prefer the original
    // API network name (e.g. "TNT") so the display matches exactly what prod shows.
    let matchedDisplayName: string | null = null;

    // 1) callSign join — the STABLE key across the OV show and the channels-service
    // feed. Channel names drift by resolution ("TNT" vs "TNT HD"), but the callSign
    // ("TNTHD") is identical on both, so this matches where a name match can't.
    const cardCallSign = channel.callSign?.toUpperCase();
    if (cardCallSign && callSignToNetwork.has(cardCallSign)) {
      matchedDisplayName = callSignToNetwork.get(cardCallSign) || channel.label;
    } else if (channelLabel && networkNamesMap.has(channelLabel)) {
      // 2) Direct name match — channelMap label lowercased is in our network names set
      matchedDisplayName = networkNamesMap.get(channelLabel) || channel.label;
    } else if (channelLabel) {
      // 3) Alias match — ovChnlPageData maps API network name → channel label
      // e.g. ovChnlPageData["FXM"] = "FX Movie Channel"
      for (const [, originalNetworkName] of Array.from(networkNamesMap.entries())) {
        const alias = ovChnlPageData?.[originalNetworkName];
        if (alias && normalizeForMatch(alias) === channelLabel) {
          matchedDisplayName = originalNetworkName;
          break;
        }
      }
    }

    if (matchedDisplayName !== null) {
      // Track the original channel.label for svodPromoMap lookup
      if (!matchedChannelLabels.includes(channel.label)) {
        matchedChannelLabels.push(channel.label);
      }

      // Accumulate this channel's packages into the deduped union (returned to the modal)
      const channelSignaturePackages = channel.signaturePackages || [];
      const channelGenrePackages = channel.genrePackages || [];
      channelSignaturePackages.forEach(ref => addPackageRef(signatureById, ref));
      channelGenrePackages.forEach(ref => addPackageRef(genreById, ref));
      // package-to-network mapping uses THIS channel's packages → the matched network
      const allPackageIds: any[] = [...channelSignaturePackages, ...channelGenrePackages];

      allPackageIds.forEach((pkgId: any) => {
        const pkgIdStr = String(pkgId?.id);
        if (!packageToNetworkMap[pkgIdStr]) {
          packageToNetworkMap[pkgIdStr] = [];
        }
        if (!packageToNetworkMap[pkgIdStr].includes(matchedDisplayName!)) {
          packageToNetworkMap[pkgIdStr].push(matchedDisplayName!);
        }
      });
    }
  });

  // Union across all matched channels (deduped by id) — the full package set the
  // show belongs to, independent of channelMap iteration order.
  const signaturePackages = Array.from(signatureById.values());
  const genrePackages = Array.from(genreById.values());
  return {
    signaturePackages,
    genrePackages,
    packageNetworkMap: packageToNetworkMap,
    matchedChannelLabels
  };
}, [show, channelMap, packageSummaries, ovChnlPageData, chnlSrc, genreConfig]);

/**
 * Extract network name for display (fallback when no packages found)
 */
export const extractDisplayNetworkName = (show: ShowData | null): string => {
  if (!show) {
    return "";
  }

  return show?.networkAttributionName ||
    show?.consumables?.[0]?.provider?.networkName ||
    show?.consumables?.[0]?.channel?.networkName ||
    show?.consumables?.[0]?.channel?.callSign ||
    show?.consumables?.[0]?.channel?.name ||
    "";
};
